if (!customElements.get('keyboard-sound-player')) {
  class KeyboardSoundPlayer extends HTMLElement {
    connectedCallback() {
      if (this.dataset.initialized === 'true') return;

      this.audio = this.querySelector('[data-sound-audio]');
      this.playButton = this.querySelector('[data-sound-play]');
      this.range = this.querySelector('[data-sound-range]');
      this.time = this.querySelector('[data-sound-time]');
      this.error = this.querySelector('[data-sound-error]');
      this.bars = [...this.querySelectorAll('[data-sound-waveform] span')];

      if (!this.audio || !this.playButton || !this.range) return;

      this.dataset.initialized = 'true';
      this.classList.add('is-ready');
      this.onPlayClick = this.togglePlayback.bind(this);
      this.onRangeInput = this.seek.bind(this);
      this.onTimeUpdate = this.updateProgress.bind(this);
      this.onEnded = this.resetPlayback.bind(this);
      this.onError = this.showError.bind(this);

      this.playButton.addEventListener('click', this.onPlayClick);
      this.range.addEventListener('input', this.onRangeInput);
      this.audio.addEventListener('timeupdate', this.onTimeUpdate);
      this.audio.addEventListener('loadedmetadata', this.onTimeUpdate);
      this.audio.addEventListener('play', this.onTimeUpdate);
      this.audio.addEventListener('pause', this.onTimeUpdate);
      this.audio.addEventListener('ended', this.onEnded);
      this.audio.addEventListener('error', this.onError);
    }

    disconnectedCallback() {
      if (!this.audio) return;
      this.audio.pause();
      this.playButton?.removeEventListener('click', this.onPlayClick);
      this.range?.removeEventListener('input', this.onRangeInput);
      this.audio.removeEventListener('timeupdate', this.onTimeUpdate);
      this.audio.removeEventListener('loadedmetadata', this.onTimeUpdate);
      this.audio.removeEventListener('play', this.onTimeUpdate);
      this.audio.removeEventListener('pause', this.onTimeUpdate);
      this.audio.removeEventListener('ended', this.onEnded);
      this.audio.removeEventListener('error', this.onError);
      delete this.dataset.initialized;
    }

    async togglePlayback() {
      this.error.hidden = true;
      if (!this.audio.paused) {
        this.audio.pause();
        return;
      }

      document.querySelectorAll('keyboard-sound-player').forEach((player) => {
        if (player !== this && player.audio) player.audio.pause();
      });

      try {
        await this.audio.play();
      } catch (error) {
        this.showError();
      }
    }

    seek() {
      if (!Number.isFinite(this.audio.duration) || this.audio.duration <= 0) return;
      this.audio.currentTime = (Number(this.range.value) / Number(this.range.max)) * this.audio.duration;
      this.updateProgress();
    }

    updateProgress() {
      const duration = Number.isFinite(this.audio.duration) ? this.audio.duration : 0;
      const progress = duration > 0 ? this.audio.currentTime / duration : 0;
      this.range.value = String(Math.round(progress * Number(this.range.max)));
      this.playButton.setAttribute('aria-pressed', String(!this.audio.paused));
      this.playButton.setAttribute(
        'aria-label',
        `${this.audio.paused ? 'Play' : 'Pause'} ${this.playButton.getAttribute('aria-label').replace(/^(Play|Pause) /, '')}`,
      );
      this.bars.forEach((bar, index) => bar.classList.toggle('is-played', index / this.bars.length <= progress));
      this.time.textContent = `${this.formatTime(this.audio.currentTime)} / ${this.formatTime(duration)}`;
    }

    resetPlayback() {
      this.audio.currentTime = 0;
      this.updateProgress();
    }

    showError() {
      this.audio.pause();
      this.playButton.setAttribute('aria-pressed', 'false');
      this.error.hidden = false;
    }

    formatTime(seconds) {
      if (!Number.isFinite(seconds)) return '0:00';
      const wholeSeconds = Math.max(0, Math.floor(seconds));
      return `${Math.floor(wholeSeconds / 60)}:${String(wholeSeconds % 60).padStart(2, '0')}`;
    }
  }

  customElements.define('keyboard-sound-player', KeyboardSoundPlayer);
}
